import { useEffect, useState } from "react";
import { Sheet } from "../../components/Sheet";
import { Section } from "../../components/AppShell";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Button, StateMessage } from "../../components/ui";
import { Checkbox, Field, Select, TextInput } from "../../components/fields";
import { useToast } from "../../components/Toast";
import {
  useAccessMembers,
  useCatalogMembers,
  useCatalogProfile,
  useCreateAccessInvite,
  useCreateCatalogInvite,
  useMe,
  useRemoveAccessMember,
  useRemoveCatalogMember,
  useUpdateCatalogProfile,
} from "../../api/queries";
import { messagesOf } from "../../api/errors";
import { fieldLabels } from "../../i18n/fieldLabels";
import { useT } from "../../i18n/strings";
import { openTelegram, shareToChat } from "../../telegram/webapp";
import type { CatalogTheme } from "../../api/types";
import { isCatalogTheme, PUBLIC_THEMES } from "../../public/themes/registry";
import s from "./catalog.module.css";

type PendingRemove =
  | { kind: "access"; telegramId: number }
  | { kind: "coedit"; telegramId: number }
  | null;

interface CatalogSettingsSheetProps {
  open: boolean;
  onClose: () => void;
}

function memberLabel(
  row: { display_name?: string | null; username?: string | null },
  fallback: string,
) {
  const name = (row.display_name || "").trim();
  if (name) return name;
  const handle = (row.username || "").trim();
  if (handle) return `@${handle}`;
  return fallback;
}

export function CatalogSettingsSheet({ open, onClose }: CatalogSettingsSheetProps) {
  const t = useT();
  const toast = useToast();
  const me = useMe();
  const access = me.data?.access;
  const profile = useCatalogProfile();
  const members = useCatalogMembers();
  const accessMembers = useAccessMembers(Boolean(access?.can_manage_access));
  const save = useUpdateCatalogProfile();
  const invite = useCreateCatalogInvite();
  const accessInvite = useCreateAccessInvite();
  const removeMember = useRemoveCatalogMember();
  const removeAccess = useRemoveAccessMember();

  const [displayName, setDisplayName] = useState("");
  const [slug, setSlug] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [theme, setTheme] = useState<CatalogTheme>("default");
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [admitLink, setAdmitLink] = useState<string | null>(null);
  const [adminLink, setAdminLink] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<PendingRemove>(null);

  useEffect(() => {
    if (!profile.data) return;
    setDisplayName(profile.data.display_name);
    setSlug(profile.data.public_slug);
    setIsPublic(profile.data.is_public);
    setTheme(profile.data.theme);
  }, [profile.data]);

  const base = me.data?.public_page?.base_url;
  const catalogLink = base && slug ? `${base}/c/${slug}` : null;

  const onSave = () => {
    save.mutate(
      {
        display_name: displayName.trim(),
        public_slug: slug.trim().toLowerCase(),
        is_public: isPublic,
        theme,
      },
      {
        onSuccess: () => toast.show(t.catalog.profileSaved),
        onError: (error) => toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
      },
    );
  };

  const onInvite = () => {
    invite.mutate(undefined, {
      onSuccess: (data) => {
        setInviteLink(data.link);
        toast.show(t.catalog.inviteCreated);
      },
      onError: (error) => toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
    });
  };

  const onAccessInvite = (kind: "admin" | "admit") => {
    accessInvite.mutate(kind, {
      onSuccess: (data) => {
        if (kind === "admin") setAdminLink(data.link);
        else setAdmitLink(data.link);
        toast.show(kind === "admin" ? t.access.adminInviteCreated : t.access.admitInviteCreated);
      },
      onError: (error) => toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
    });
  };

  const linkBox = (link: string | null, label: string, shareText: string) =>
    link && (
      <div className={s.inviteBox}>
        <p className={s.memberMeta}>{label}</p>
        <p className={s.inviteLink}>{link}</p>
        <div className={s.row}>
          <Button
            icon="copy"
            onClick={() => {
              void navigator.clipboard.writeText(link);
              toast.show(t.share.copied);
            }}
          >
            {t.share.copy}
          </Button>
          <Button icon="share" onClick={() => shareToChat(link, shareText)}>
            {t.share.send}
          </Button>
          <Button icon="openLink" onClick={() => openTelegram(link)}>
            {t.share.open}
          </Button>
        </div>
      </div>
    );

  const confirmRemove = () => {
    if (!pendingRemove) return;
    const target = pendingRemove;
    if (target.kind === "access") {
      removeAccess.mutate(target.telegramId, {
        onSuccess: () => {
          setPendingRemove(null);
          toast.show(t.access.memberRemoved);
        },
        onError: (error) =>
          toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
      });
      return;
    }
    removeMember.mutate(target.telegramId, {
      onSuccess: () => {
        setPendingRemove(null);
        toast.show(t.catalog.memberRemoved);
      },
      onError: (error) =>
        toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
    });
  };

  return (
    <>
      <Sheet open={open} onClose={onClose} title={t.catalog.settingsTitle}>
        {profile.isPending && <StateMessage icon="spinner" title={t.loading} body="" />}
        {profile.error && (
          <StateMessage
            icon="alert"
            title={t.errors.title}
            body={messagesOf(profile.error)[0] ?? t.errors.network}
          />
        )}
        {profile.data && (
          <div className={s.panel}>
            <Section title={t.catalog.publicCatalogTitle} icon="qr">
              <Field label={fieldLabels.display_name} optional>
                <TextInput auto value={displayName} onChange={setDisplayName} />
              </Field>
              <Field label={fieldLabels.public_slug}>
                <TextInput value={slug} onChange={setSlug} />
              </Field>
              <Field label={fieldLabels.catalog_theme}>
                <Select
                  value={theme}
                  options={PUBLIC_THEMES.map((row) => ({
                    value: row.id,
                    label: t.catalog.theme[row.labelKey],
                  }))}
                  onChange={(value) => {
                    if (isCatalogTheme(value)) setTheme(value);
                  }}
                />
              </Field>
              <Checkbox checked={isPublic} onChange={setIsPublic}>
                {t.catalog.catalogPublicToggle}
              </Checkbox>
              {catalogLink && isPublic && <p className={s.inviteLink}>{catalogLink}</p>}
              <Button icon="check" disabled={save.isPending} onClick={onSave}>
                {save.isPending ? t.actions.saving : t.actions.save}
              </Button>
            </Section>

            {access?.can_manage_access && (
              <Section title={t.access.title} icon="artist">
                <p className={s.memberMeta}>{t.access.hint}</p>
                {accessMembers.data?.map((row) => (
                  <div key={row.telegram_id} className={s.memberRow}>
                    <div>
                      <p className={s.memberName}>
                        {memberLabel(row, t.access.anonymousMember)}
                      </p>
                      <p className={s.memberMeta}>
                        {row.role === "admin" ? t.access.roleAdmin : t.access.roleUser}
                        {row.display_name && row.username ? ` · @${row.username}` : ""}
                      </p>
                    </div>
                    {(access.role === "super" || row.role === "user") && (
                      <Button
                        variant="ghost"
                        icon="delete"
                        disabled={removeAccess.isPending}
                        onClick={() =>
                          setPendingRemove({
                            kind: "access",
                            telegramId: row.telegram_id,
                          })
                        }
                      >
                        {t.actions.remove}
                      </Button>
                    )}
                  </div>
                ))}
                {!accessMembers.isPending && (accessMembers.data?.length ?? 0) === 0 && (
                  <p className={s.memberMeta}>{t.access.noMembers}</p>
                )}

                {access.can_mint_admit && (
                  <div className={s.inviteKind}>
                    <p className={s.inviteKindTitle}>{t.access.admitTitle}</p>
                    <p className={s.memberMeta}>{t.access.admitHint}</p>
                    <Button
                      icon="share"
                      disabled={accessInvite.isPending}
                      onClick={() => onAccessInvite("admit")}
                    >
                      {t.access.admitInviteCreate}
                    </Button>
                    {linkBox(admitLink, t.access.admitLinkLabel, t.access.admitShareText)}
                  </div>
                )}

                {access.can_mint_admin && (
                  <div className={s.inviteKind}>
                    <p className={s.inviteKindTitle}>{t.access.adminTitle}</p>
                    <p className={s.memberMeta}>{t.access.adminHint}</p>
                    <Button
                      icon="share"
                      disabled={accessInvite.isPending}
                      onClick={() => onAccessInvite("admin")}
                    >
                      {t.access.adminInviteCreate}
                    </Button>
                    {linkBox(adminLink, t.access.adminLinkLabel, t.access.adminShareText)}
                  </div>
                )}
              </Section>
            )}

            {access?.can_invite_coedit !== false && (
              <>
                <Section title={t.catalog.membersTitle} icon="artist">
                  {members.isPending && <p>{t.loading}</p>}
                  {members.data?.map((member) => (
                    <div key={member.telegram_id} className={s.memberRow}>
                      <div>
                        <p className={s.memberName}>
                          {memberLabel(member, t.catalog.anonymousMember)}
                        </p>
                        {member.display_name && member.username && (
                          <p className={s.memberMeta}>@{member.username}</p>
                        )}
                      </div>
                      {member.telegram_id !== me.data?.user.telegram_id && (
                        <Button
                          variant="ghost"
                          icon="delete"
                          disabled={removeMember.isPending}
                          onClick={() =>
                            setPendingRemove({
                              kind: "coedit",
                              telegramId: member.telegram_id,
                            })
                          }
                        >
                          {t.actions.remove}
                        </Button>
                      )}
                    </div>
                  ))}
                  {!members.isPending && members.data?.length === 0 && (
                    <p className={s.memberMeta}>{t.catalog.noMembers}</p>
                  )}
                </Section>

                <Section title={t.catalog.inviteTitle} icon="share">
                  <p className={s.memberMeta}>{t.catalog.inviteHint}</p>
                  <Button icon="share" disabled={invite.isPending} onClick={onInvite}>
                    {invite.isPending ? t.catalog.inviteCreating : t.catalog.inviteCreate}
                  </Button>
                  {linkBox(inviteLink, t.catalog.inviteLinkLabel, t.catalog.inviteShareText)}
                </Section>
              </>
            )}

            {access?.role === "super" && me.data?.user.telegram_id != null && (
              <Section title={t.catalog.yourTelegramId} icon="info">
                <div className={s.inviteBox}>
                  <p className={s.inviteLink}>{me.data.user.telegram_id}</p>
                  <Button
                    icon="copy"
                    onClick={() => {
                      const id = me.data?.user.telegram_id;
                      if (id == null) return;
                      void navigator.clipboard.writeText(String(id));
                      toast.show(t.share.copied);
                    }}
                  >
                    {t.share.copy}
                  </Button>
                </div>
              </Section>
            )}
          </div>
        )}
      </Sheet>
      <ConfirmDialog
        open={pendingRemove != null}
        title={
          pendingRemove?.kind === "coedit" ? t.catalog.removeCoeditTitle : t.access.removeTitle
        }
        body={
          pendingRemove?.kind === "coedit" ? t.catalog.removeCoeditBody : t.access.removeBody
        }
        confirmLabel={
          pendingRemove?.kind === "coedit"
            ? t.catalog.removeCoeditConfirm
            : t.access.removeConfirm
        }
        onConfirm={confirmRemove}
        onCancel={() => setPendingRemove(null)}
      />
    </>
  );
}
